import { api } from "@/lib/api";
import {
  UserSubscriptionInput,
  UserSubscriptionOutput,
  UserSubscriptionModelOutput,
  UserSubscriptionUpdateInput,
  UserSubscriptionGetAllOutput,
  UserSubscriptionFeature,
  UserSubscriptionFeaturesResponse,
} from "@/types/userSubscription.types";

export interface UserSubscriptionResponse {
  status: string;
  message: string;
  data: UserSubscriptionGetAllOutput | UserSubscriptionGetAllOutput[] | UserSubscriptionOutput | UserSubscriptionOutput[];
}

export interface UserSubscriptionModelResponse {
  status: string;
  message: string;
  data: UserSubscriptionModelOutput | UserSubscriptionModelOutput[];
}

export const userSubscriptionService = {
  async getAllUserSubscriptions(): Promise<UserSubscriptionGetAllOutput[]> {
    try {
      const response = await api.get<{
        status: string;
        message: string;
        data: UserSubscriptionGetAllOutput | UserSubscriptionGetAllOutput[];
      }>("/user-subscriptions");
      return Array.isArray(response.data.data)
        ? response.data.data
        : [response.data.data];
    } catch (error) {
      console.error("Error fetching user subscriptions:", error);
      throw error;
    }
  },

  async getUserSubscriptionById(
    id: string
  ): Promise<UserSubscriptionModelOutput> {
    try {
      const response = await api.get<UserSubscriptionModelResponse>(
        `/user-subscriptions/${id}`
      );
      return Array.isArray(response.data.data)
        ? response.data.data[0]
        : response.data.data;
    } catch (error) {
      console.error("Error fetching user subscription:", error);
      throw error;
    }
  },

  async createUserSubscription(
    input: UserSubscriptionInput
  ): Promise<UserSubscriptionOutput> {
    try {
      // Ensure subscriptionId is the ID, not the name
      const payload = {
        ...input,
        subscriptionId: input.subscriptionId, // Explicitly use the ID
      };

      const response = await api.post<UserSubscriptionResponse>(
        "/user-subscriptions",
        payload
      );
      return Array.isArray(response.data.data)
        ? response.data.data[0]
        : response.data.data;
    } catch (error) {
      console.error("Error creating user subscription:", error);
      throw error;
    }
  },

  async updateUserSubscription(
    id: string,
    input: UserSubscriptionUpdateInput
  ): Promise<UserSubscriptionModelOutput> {
    try {
      const response = await api.put<UserSubscriptionModelResponse>(
        `/user-subscriptions/${id}`,
        input
      );
      return Array.isArray(response.data.data)
        ? response.data.data[0]
        : response.data.data;
    } catch (error) {
      console.error("Error updating user subscription:", error);
      throw error;
    }
  },

  async updateUserSubscriptionIsActive(
    id: string,
    isActive: boolean
  ): Promise<UserSubscriptionModelOutput> {
    try {
      const response = await api.patch<UserSubscriptionModelResponse>(
        `/user-subscriptions/${id}/is-active`,
        { isActive }
      );
      return Array.isArray(response.data.data)
        ? response.data.data[0]
        : response.data.data;
    } catch (error) {
      console.error("Error updating user subscription isActive status:", error);
      throw error;
    }
  },

  async deleteUserSubscription(id: string): Promise<void> {
    try {
      await api.delete(`/user-subscriptions/${id}`);
    } catch (error) {
      console.error("Error deleting user subscription:", error);
      throw error;
    }
  },

  async getUserSubscriptionFeatures(
    userSubId: string
  ): Promise<UserSubscriptionFeature[]> {
    try {
      const response = await api.get<UserSubscriptionFeaturesResponse>(
        `/user-subscriptions/${userSubId}/features`
      );
      return response.data.data.features;
    } catch (error) {
      console.error("Error fetching user subscription features:", error);
      throw error;
    }
  },
};
